import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  format,
  parse,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import RevenueForm from './RevenueForm';
import Skeleton from './Skeleton';
import { useTooltip } from '../lib/useTooltip';
import { logActivity } from '../lib/activity';
import { useAccentColor } from '../lib/useAccentColor';
import { Coins, MessageSquare } from 'lucide-react';

interface RevenueEntry {
  id: string;
  date: string;
  cash_revenue: number;
  card_revenue: number;
  invoice_revenue: number;
  notes?: string;
}

export default function MonthView() {
  const { year = '', month = '' } = useParams();
  const { tooltip } = useTooltip();
  const [entries, setEntries] = useState<Record<string, RevenueEntry>>({});
  const [tipsMap, setTipsMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showTips, setShowTips] = useState(false);
  const { accentColor } = useAccentColor();

  const currentDate = parse(`${year}-${month}`, 'yyyy-MM', new Date());
  const days = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate),
  });

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: settings } = await supabase
          .from('settings')
          .select('show_tips')
          .eq('user_id', user.id)
          .maybeSingle();

        if (isMounted && settings) {
          setShowTips(settings.show_tips ?? true);
        }

        const { data, error } = await supabase
          .from('revenue_entries')
          .select('*')
          .gte('date', format(startOfMonth(currentDate), 'yyyy-MM-dd'))
          .lte('date', format(endOfMonth(currentDate), 'yyyy-MM-dd'));

        if (isMounted) {
          if (!error && data) {
            const entriesMap = data.reduce((acc, entry) => {
              acc[entry.date] = entry;
              return acc;
            }, {} as Record<string, RevenueEntry>);
            setEntries(entriesMap);
          }

          if (settings?.show_tips ?? true) {
            const { data: tipsData } = await supabase
              .from('hidden_revenue')
              .select('date, amount')
              .gte('date', format(startOfMonth(currentDate), 'yyyy-MM-dd'))
              .lte('date', format(endOfMonth(currentDate), 'yyyy-MM-dd'));

            if (tipsData) {
              const tips: Record<string, number> = {};
              tipsData.forEach(tip => {
                if (!tips[tip.date]) {
                  tips[tip.date] = 0;
                }
                tips[tip.date] += tip.amount;
              });
              setTipsMap(tips);
            }
          }

          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Check if we should open a revenue form (from mobile navigation)
    const formDateToOpen = sessionStorage.getItem('openRevenueForm');
    if (formDateToOpen) {
      setSelectedDate(formDateToOpen);
      sessionStorage.removeItem('openRevenueForm');
    }

    return () => {
      isMounted = false;
    };
  }, [currentDate]);

  const totals = days.reduce(
    (acc, day) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const entry = entries[dateStr];
      const tips = showTips ? (tipsMap[dateStr] || 0) : 0;
      if (entry) {
        acc.cash += entry.cash_revenue;
        acc.card += entry.card_revenue;
        acc.invoice += entry.invoice_revenue;
        acc.total += entry.cash_revenue + entry.card_revenue + entry.invoice_revenue;
      }
      acc.tips += tips;
      return acc;
    },
    { cash: 0, card: 0, invoice: 0, total: 0, tips: 0 }
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30">
                <tr>
                  {[...Array(7)].map((_, i) => (
                    <th key={i} className="px-6 py-3">
                      <Skeleton className="h-4 w-24" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {[...Array(7)].map((_, colIndex) => (
                      <td key={colIndex} className="px-6 py-4">
                        <Skeleton className="h-6 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {format(currentDate, 'MMMM yyyy', { locale: nl })}
        </h1>
        <button
          onClick={async () => {
            const newShowTips = !showTips;
            setShowTips(newShowTips);

            try {
              const { data: { user } } = await supabase.auth.getUser();
              if (!user) return;

              await supabase
                .from('settings')
                .update({ show_tips: newShowTips })
                .eq('user_id', user.id);

              if (newShowTips) {
                const { data: tipsData } = await supabase
                  .from('hidden_revenue')
                  .select('date, amount')
                  .gte('date', format(startOfMonth(currentDate), 'yyyy-MM-dd'))
                  .lte('date', format(endOfMonth(currentDate), 'yyyy-MM-dd'));

                if (tipsData) {
                  const tips: Record<string, number> = {};
                  tipsData.forEach(tip => {
                    if (!tips[tip.date]) {
                      tips[tip.date] = 0;
                    }
                    tips[tip.date] += tip.amount;
                  });
                  setTipsMap(tips);
                }
              } else {
                setTipsMap({});
              }
            } catch (error) {
              console.error('Error updating show_tips setting:', error);
            }
          }}
          className={`w-full lg:w-auto flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-medium transition-colors ${
            showTips
              ? 'bg-green-500/20 dark:bg-green-500/30 text-green-700 dark:text-green-300'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Coins className="h-4 w-4 mr-2" />
          <span>Fooi {showTips ? 'verbergen' : 'tonen'}</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Datum
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Dag
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Contant
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Pin
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Rekening
                </th>
                {showTips && (
                  <th className="px-6 py-4 text-right text-xs font-semibold text-red-500 dark:text-red-400 uppercase tracking-wider">
                    Fooi
                  </th>
                )}
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Totaal
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {days.map((day) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const entry = entries[dateStr];
                const tips = showTips ? (tipsMap[dateStr] || 0) : 0;
                const total = entry
                  ? entry.cash_revenue + entry.card_revenue + entry.invoice_revenue + tips
                  : tips;

                return (
                  <tr key={dateStr} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      <div className="flex items-center gap-1.5">
                        {format(day, 'dd/MM/yyyy')}
                        {entry?.notes && (
                          <span
                            {...tooltip(`note-${dateStr}`, entry.notes)}
                            className="inline-flex items-center"
                          >
                            <MessageSquare className="h-3.5 w-3.5 text-amber-400" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {format(day, 'EEEE', { locale: nl })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{entry?.cash_revenue?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{entry?.card_revenue?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{entry?.invoice_revenue?.toFixed(2) || '0.00'}
                    </td>
                    {showTips && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-500 dark:text-red-400 font-medium">
                        €{tips.toFixed(2)}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-accent">
                      €{total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setSelectedDate(dateStr)}
                        {...tooltip(`add-revenue-${dateStr}`, entry ? 'Bewerk omzet' : 'Voeg omzet toe')}
                        className={`inline-flex items-center justify-center min-w-[70px] px-2.5 py-1.5 rounded text-xs font-medium transition-colors duration-200 ${
                          entry
                            ? 'bg-blue-500/10 hover:bg-blue-500/20 dark:bg-blue-400/10 dark:hover:bg-blue-400/20 text-blue-700 dark:text-blue-400'
                            : 'bg-green-500/10 hover:bg-green-500/20 dark:bg-green-400/10 dark:hover:bg-green-400/20 text-green-700 dark:text-green-400'
                        }`}
                      >
                        {entry ? 'Bewerken' : 'Toevoegen'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-blue-50/50 dark:bg-blue-900/10">
                <td
                  colSpan={2}
                  className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-800 dark:text-blue-200"
                >
                  Maandtotalen
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{totals.cash.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{totals.card.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{totals.invoice.toFixed(2)}
                </td>
                {showTips && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-red-500 dark:text-red-400">
                    €{totals.tips.toFixed(2)}
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-blue-800 dark:text-blue-200">
                  €{(totals.total + totals.tips).toFixed(2)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {selectedDate && (
        <RevenueForm
          date={selectedDate}
          entry={entries[selectedDate]}
          onClose={() => setSelectedDate(null)}
          onSave={(newEntry) => {
            setEntries((prev) => ({
              ...prev,
              [selectedDate]: newEntry,
            }));
            setSelectedDate(null);
          }}
        />
      )}
    </div>
  );
}